import { useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { useFarmStore } from "@/features/farms/stores/farmStore"
import { useAuthStore } from "@/features/auth/stores/authStore"
import { farmsApi } from "@/features/farms/api/farmsApi"
import { pondsApi } from "@/features/ponds/api/pondsApi"
import { queryKeys } from "@/shared/api/queryClient"
import { clearPondId, saveFarmId } from "@/shared/lib/pondIdStorage"

/**
 * Syncs the selected farm from the URL with the Zustand store
 * Fetches farm data if farmId is in URL but not in store
 * Uses user-scoped localStorage keys
 */
export function useFarmFromUrl() {
  const { farmId: rawFarmId } = useParams<{ farmId: string }>()
  const { selectedFarm, setSelectedFarm } = useFarmStore()
  const { user, isLoading: isAuthLoading } = useAuthStore()

  // Trim and clean the farmId from URL to handle any whitespace or trailing characters
  const farmId = rawFarmId?.trim()

  // Debug logging for farm ID
  if (rawFarmId && rawFarmId !== farmId) {
    console.warn('[useFarmFromUrl] Farm ID had extra characters:', {
      raw: rawFarmId,
      rawLength: rawFarmId.length,
      cleaned: farmId,
      cleanedLength: farmId?.length
    })
  }

  if (farmId && farmId.length !== 26) {
    console.error('[useFarmFromUrl] Invalid farm ID length:', {
      farmId,
      length: farmId.length,
      expected: 26
    })
  }

  // Fetch farm data if we have farmId in URL (wait for auth to complete first)
  const { data: farm, isLoading: isFetching, error, isError } = useQuery({
    queryKey: queryKeys.farms.detail(farmId || ""),
    queryFn: () => farmsApi.get(farmId!),
    enabled: !!farmId && !isAuthLoading,
    retry: 1, // Only retry once on failure
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  })

  // Sync URL farmId with store
  useEffect(() => {
    if (farm && farm.id !== selectedFarm?.id) {
      // Clear pond ID when switching farms (user-scoped)
      if (user?.id) {
        clearPondId(user.id)
      }

      setSelectedFarm(farm)
      // Save to localStorage for backward compat (user-scoped)
      if (user?.id) {
        saveFarmId(user.id, farm.id)
      }
    }
  }, [farm, selectedFarm, setSelectedFarm, user?.id])

  // Also sync from store if farmId matches (user navigated via UI)
  useEffect(() => {
    if (selectedFarm && farmId && selectedFarm.id === farmId && !farm) {
      // Store already has the right farm, no need to fetch
    }
  }, [selectedFarm, farmId, farm])

  return {
    farmId,
    farm: farm || (selectedFarm?.id === farmId ? selectedFarm : undefined),
    isLoading: isAuthLoading || isFetching,
    isError,
    error,
    setFarm: (newFarm: typeof selectedFarm) => {
      // Clear pond ID when manually switching farms (user-scoped)
      if (newFarm?.id !== selectedFarm?.id && user?.id) {
        clearPondId(user.id)
      }

      setSelectedFarm(newFarm)
      if (newFarm && user?.id) {
        saveFarmId(user.id, newFarm.id)
      }
    },
  }
}

/**
 * Syncs the selected pond from the URL
 * Returns pond data and provides navigation helpers
 */
export function usePondFromUrl(farmId: string | undefined) {
  const { pondId } = useParams<{ pondId: string }>()
  console.log('usePondFromUrl', { farmId, pondId })
  const navigate = useNavigate()

  // Fetch pond data if we have pondId in URL
  const { data: pond, isLoading } = useQuery({
    queryKey: queryKeys.ponds.detail(farmId || "", pondId || ""),
    queryFn: () => pondsApi.get(farmId!, pondId!),
    enabled: !!farmId && !!pondId,
  })

  return {
    pondId,
    pond,
    isLoading,
    // Helper to navigate to a pond (keeps modal pattern)
    navigateToPond: (newPondId: string) => {
      if (farmId) {
        navigate(`/farms/${farmId}/ponds/${newPondId}`)
      }
    },
    // Helper to close pond modal
    closePond: () => {
      if (farmId) {
        navigate(`/farms/${farmId}/ponds`)
      }
    },
  }
}

/**
 * Hook for record-level deeplinking (tests, checks, etc.)
 * Generic hook that works for any record type
 */
export function useRecordFromUrl(recordParam: string) {
  const params = useParams()
  const recordId = params[recordParam as keyof typeof params]

  return {
    recordId,
    hasRecord: !!recordId,
  }
}
